import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, X, CornerDownLeft, Clock } from "lucide-react";
import { cn } from "@/lib/cn";
import { ProductImage } from "@/components/atoms";
import { useProducts } from "@/hooks/use-products";
import { searchProducts } from "@/lib/product-search";
import { formatPrice } from "@/lib/format";

const RECENT_KEY = "drburns-recent-searches-v1";
const MAX_RECENT = 4;
/* Baymard: ~10 suggestions on desktop, 4-8 on mobile, and NO inner scrollbar — past that users
   hit choice paralysis and start ignoring the list entirely. This catalogue is far smaller than
   that ceiling, so 8 is a cap that will not be reached rather than a real constraint. */
const MAX_RESULTS = 8;

function readRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(v)
      ? v.filter((x) => typeof x === "string").slice(0, MAX_RECENT)
      : [];
  } catch {
    return []; // a corrupt or unavailable store must never break search
  }
}

/**
 * DRB-67 — full-screen search, opened from a single icon in the nav.
 *
 * WHY A MODAL RATHER THAN AN INLINE DROPDOWN. Baymard's autocomplete research (only 19% of
 * ecommerce sites get this right, despite 80% shipping it) recommends darkening the page while
 * search is active so the results are not competing with page content. A modal is the strongest
 * form of that, and it takes the input out of the header, which is what makes the nav clean.
 *
 * WHY PRODUCT RESULTS WITH THUMBNAILS, WHICH BAYMARD ADVISES AGAINST. That guidance is about
 * QUERY suggestions on a large catalogue, where thumbnails are visual noise competing with the
 * text you are trying to scan. This catalogue has two active products: there is no query space
 * worth predicting, so the products themselves ARE the answer, and an image identifies a jar or
 * a bottle faster than its name does. Different problem, different pattern.
 *
 * The empty state carries chips derived from the LIVE catalogue plus the visitor's recent
 * searches, so opening the modal is never a blank box. Chips come from real product terms, so
 * every one of them returns results — a suggestion that leads to "no matches" is worse than none.
 */
export function SearchModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(-1);
  const [recent, setRecent] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data: products } = useProducts();
  const hits = useMemo(
    () => searchProducts(products ?? [], q, MAX_RESULTS),
    [products, q],
  );

  /* Chips from the live catalogue: the distinct condition and category terms actually in use.
     Derived rather than hardcoded, so they cannot rot into suggestions that return nothing when
     the catalogue changes. */
  const chips = useMemo(() => {
    const set = new Set<string>();
    for (const p of products ?? []) {
      if (p.condition) set.add(p.condition);
      if (p.category) set.add(p.category);
    }
    return [...set].slice(0, 6);
  }, [products]);

  useEffect(() => {
    if (open) {
      setQ("");
      setActive(-1);
      setRecent(readRecent());
      // Focus after paint, or the browser may not move focus into a just-mounted node.
      requestAnimationFrame(() => inputRef.current?.focus());
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden"; // no scrolling the page behind the overlay
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  /*
   * A route change must close it, or navigating from a result leaves the overlay covering the
   * page. But an effect keyed on pathname ALSO runs on mount, so the naive version called
   * onClose() the instant the modal opened — it would have opened and immediately closed itself
   * in the browser. Caught by the component test, which saw onClose fire with no interaction.
   * Skip the first run and react only to an actual change.
   */
  const mountedPath = useRef(pathname);
  useEffect(() => {
    if (mountedPath.current === pathname) return;
    mountedPath.current = pathname;
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => setActive(-1), [q]);

  const record = useCallback((term: string) => {
    const t = term.trim();
    if (!t) return;
    const next = [
      t,
      ...readRecent().filter((x) => x.toLowerCase() !== t.toLowerCase()),
    ].slice(0, MAX_RECENT);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* private browsing — remembering a search is not worth breaking search over */
    }
  }, []);

  const go = useCallback(
    (to: string, term: string) => {
      record(term);
      onClose();
      navigate(to);
    },
    [navigate, onClose, record],
  );

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (active >= 0 && hits[active]) {
        go(`/products/${hits[active]!.product.slug}`, q);
      } else if (q.trim()) {
        go(`/search?q=${encodeURIComponent(q.trim())}`, q);
      }
      return;
    }
    if (hits.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? hits.length - 1 : i - 1));
    }
  }

  // Focus trap: Tab must not escape to the page underneath a modal.
  function onKeyDownCapture(e: React.KeyboardEvent) {
    if (e.key !== "Tab" || !panelRef.current) return;
    const f = panelRef.current.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])',
    );
    if (f.length === 0) return;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (!open) return null;
  const showResults = q.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search products"
      data-testid="search-modal"
      onKeyDownCapture={onKeyDownCapture}
      // Only a click on the BACKDROP closes it. Without the target/currentTarget guard a click
      // that bubbled up from the input or a result row would dismiss the modal under the user.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[110] flex items-start justify-center bg-night-blue/40 px-4 pt-[8vh] backdrop-blur-sm"
    >
      <div
        ref={panelRef}
        /* Hugs its content. It used to stretch to full height with two results sitting above a
           large empty white area, and the cause was the PARENT, not this element: a flex container
           defaults to align-items:stretch, so no class here could have shortened it. The backdrop
           carries items-start; the cap lives on the list below. */
        className="w-full max-w-2xl overflow-hidden rounded-card bg-white shadow-2xl"
      >
        <div className="flex items-center gap-3 border-b border-night-blue/10 px-4">
          <Search
            size={20}
            className="shrink-0 text-night-blue/70"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search products…"
            aria-label="Search products"
            role="combobox"
            aria-expanded={showResults}
            aria-controls="search-modal-results"
            aria-autocomplete="list"
            aria-activedescendant={
              active >= 0 && hits[active] ? `search-opt-${active}` : undefined
            }
            autoComplete="off"
            className="h-14 flex-1 bg-transparent text-base text-night-blue outline-none placeholder:text-night-blue/70"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-night-blue/70 hover:bg-medical-grey hover:text-night-blue"
          >
            <X size={20} />
          </button>
        </div>

        <div
          id="search-modal-results"
          role="listbox"
          aria-label="Search results"
          className="max-h-[60vh] overflow-y-auto"
        >
          {!showResults ? (
            <div className="p-4">
              {recent.length > 0 && (
                <>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-night-blue/70">
                    <Clock size={12} /> Recent
                  </p>
                  <div className="mb-4 flex flex-wrap gap-2">
                    {recent.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setQ(r)}
                        className="rounded-pill border border-night-blue/15 px-3 py-1.5 text-sm text-night-blue hover:border-leaf-green-ink"
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {chips.length > 0 && (
                <>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-night-blue/70">
                    Browse by
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {chips.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setQ(c)}
                        className="rounded-pill bg-medical-grey px-3 py-1.5 text-sm capitalize text-night-blue hover:bg-leaf-green/10"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : hits.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-night-blue/70">
              No products match “{q.trim()}”.
            </p>
          ) : (
            <>
              <ul>
                {hits.map((h, i) => (
                  <li key={h.product.id}>
                    <button
                      type="button"
                      id={`search-opt-${i}`}
                      role="option"
                      aria-selected={i === active}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(`/products/${h.product.slug}`, q)}
                      className={cn(
                        "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                        i === active ? "bg-medical-grey" : "bg-white",
                      )}
                    >
                      <ProductImage
                        src={h.thumbnail}
                        alt=""
                        name={h.product.name}
                        className="h-12 w-12 shrink-0"
                        imgClassName="object-cover"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-night-blue">
                          {h.product.name}
                        </span>
                        {h.product.condition && (
                          <span className="block truncate text-xs text-night-blue/70">
                            {h.product.condition}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 font-bold text-night-blue">
                        {formatPrice(h.priceCents)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() =>
                  go(`/search?q=${encodeURIComponent(q.trim())}`, q)
                }
                className="flex w-full items-center justify-between gap-2 border-t border-night-blue/10 px-4 py-3 text-left text-sm font-medium text-night-blue/70 hover:bg-medical-grey"
              >
                <span>
                  See all {hits.length} result{hits.length === 1 ? "" : "s"}
                </span>
                <CornerDownLeft size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
